import * as THREE from "three";
import { PEAR_TREE } from "./config";
import { makeLabel } from "./labels";

const BARK = 0x5a4636;
const BARK_DARK = 0x3f3228;
const LEAF = [0x2f5a32, 0x3d6b38, 0x4a7a40, 0x355e34];
const PEAR = 0xc8c04a;
const PEAR_BLUSH = 0xc4a03a;
const MULCH = 0x4a3a2c;

export function buildPearTree(): THREE.Group {
  const tree = new THREE.Group();
  tree.name = "pear-tree";
  tree.position.set(PEAR_TREE.x, 0, PEAR_TREE.z);

  const bark = new THREE.MeshStandardMaterial({
    color: BARK,
    roughness: 0.92,
    metalness: 0.02,
  });
  const barkDark = new THREE.MeshStandardMaterial({
    color: BARK_DARK,
    roughness: 0.95,
    metalness: 0.02,
  });

  const flare = new THREE.Mesh(
    new THREE.CylinderGeometry(0.42, 0.62, 0.45, 10),
    barkDark,
  );
  flare.position.y = 0.22;
  flare.castShadow = true;
  flare.receiveShadow = true;
  tree.add(flare);

  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.46, 4.4, 10), bark);
  trunk.position.y = 2.4;
  trunk.castShadow = true;
  trunk.receiveShadow = true;
  tree.add(trunk);

  const fork = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.26, 2.6, 8), bark);
  fork.position.set(0.15, 5.3, 0.05);
  fork.rotation.z = -0.18;
  fork.rotation.x = 0.08;
  fork.castShadow = true;
  tree.add(fork);

  const limbs: Array<[number, number, number, number, number, number, number]> = [
    [0.9, 5.6, 0.2, 0.55, 0.12, 0.2, 1.8],
    [-0.7, 5.4, -0.35, -0.7, 0.1, -0.35, 1.6],
    [0.15, 6.4, 0.7, 0.1, 0.45, 0.55, 1.7],
    [0.35, 6.8, -0.55, 0.25, 0.35, -0.5, 1.5],
    [-0.2, 7.2, 0.15, -0.15, 0.55, 0.1, 1.4],
  ];
  for (const [x, y, z, rx, rz, ry, len] of limbs) {
    const limb = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.14, len, 7), bark);
    limb.position.set(x, y, z);
    limb.rotation.set(rx, ry, rz);
    limb.castShadow = true;
    tree.add(limb);
  }

  const leafMats = LEAF.map(
    (c) =>
      new THREE.MeshStandardMaterial({
        color: c,
        roughness: 0.78,
        metalness: 0.0,
      }),
  );

  const crown = new THREE.Group();
  crown.position.y = 8.15;
  const layers: Array<[number, number, number, number, number]> = [
    [0, -3.6, 2.4, 1.5, 2.4],
    [0.3, -2.1, 3.6, 1.9, 3.5],
    [-0.25, -0.4, 4.5, 2.2, 4.4],
    [0.15, 1.3, 3.5, 1.8, 3.4],
    [0, 2.7, 1.9, 1.4, 1.9],
  ];
  layers.forEach(([x, y, sx, sy, sz], i) => {
    const clump = new THREE.Mesh(new THREE.IcosahedronGeometry(1, 1), leafMats[i % leafMats.length]);
    clump.position.set(x, y, 0);
    clump.scale.set(sx, sy, sz);
    clump.castShadow = true;
    clump.receiveShadow = true;
    crown.add(clump);
  });

  const rnd = rng(7);
  for (let i = 0; i < 14; i++) {
    const a = rnd() * Math.PI * 2;
    const h = (rnd() - 0.45) * 5.2;
    const r = 1.6 + rnd() * 2.4 * (1 - Math.abs(h) / 6);
    const clump = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.7 + rnd() * 0.45, 0),
      leafMats[i % leafMats.length],
    );
    clump.position.set(Math.cos(a) * r, h, Math.sin(a) * r);
    clump.rotation.set(rnd() * 0.8, rnd() * 2, rnd() * 0.8);
    clump.castShadow = true;
    crown.add(clump);
  }
  tree.add(crown);

  const pearGeo = pearGeometry();
  const pearMat = new THREE.MeshStandardMaterial({
    color: PEAR,
    roughness: 0.55,
    metalness: 0.04,
  });
  const blush = new THREE.MeshStandardMaterial({
    color: PEAR_BLUSH,
    roughness: 0.55,
    metalness: 0.04,
  });
  for (let i = 0; i < 22; i++) {
    const a = rnd() * Math.PI * 2;
    const h = 5.2 + rnd() * 4.8;
    const r = 1.1 + rnd() * 2.6;
    const fruit = new THREE.Mesh(pearGeo, i % 4 === 0 ? blush : pearMat);
    fruit.position.set(Math.cos(a) * r, h, Math.sin(a) * r);
    fruit.rotation.set(0.15 + rnd() * 0.4, rnd() * 2, (rnd() - 0.5) * 0.4);
    fruit.scale.setScalar(0.85 + rnd() * 0.4);
    fruit.castShadow = true;
    tree.add(fruit);
  }

  const mulch = new THREE.Mesh(
    new THREE.CylinderGeometry(1.6, 1.7, 0.08, 16),
    new THREE.MeshStandardMaterial({ color: MULCH, roughness: 1, metalness: 0 }),
  );
  mulch.position.y = 0.05;
  mulch.receiveShadow = true;
  tree.add(mulch);

  const label = makeLabel("Pear tree", "tree-label");
  label.position.set(0, 13.2, 0);
  tree.add(label);

  return tree;
}

function pearGeometry(): THREE.LatheGeometry {
  const pts = [
    new THREE.Vector2(0.0, 0.0),
    new THREE.Vector2(0.045, 0.01),
    new THREE.Vector2(0.08, 0.05),
    new THREE.Vector2(0.09, 0.1),
    new THREE.Vector2(0.07, 0.15),
    new THREE.Vector2(0.04, 0.2),
    new THREE.Vector2(0.022, 0.23),
    new THREE.Vector2(0.0, 0.25),
  ];
  return new THREE.LatheGeometry(pts, 8);
}

function rng(seed: number): () => number {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
